/**
 * CUSTOM
 * This code has been developed for this solution.
 */
import {
  UserCreateData,
  UsersQueryData,
  UserUpdateData,
} from "../../core/models/User";
import { notify } from "../../libs/observables/notification";
import NetworkServiceFactory from "../../core/services/network/NetworkService";
import { DEFAULT_QUERY_DATA } from "../../core/services/network/NetworkTypes";
import { useAuthContext } from "../authentication/AuthenticationContext";
import { useUsersContext } from "./UsersContext";

const useUsersActions = () => {
  const {
    setUsersResponseData,
    setUsersLoading,
    setSelectedUser,
    setUsersError,
    usersResponseData,
    oidcUserFound,
    setOidcUserFound,
  } = useUsersContext();
  const { userInfo } = useAuthContext();

  const dispatchGetUsers = async (
    query: UsersQueryData = DEFAULT_QUERY_DATA()
  ) => {
    let cancelLoading = false;
    setTimeout(() => {
      if (!cancelLoading) {
        setUsersLoading(true);
      }
    }, 400);
    const networkService = NetworkServiceFactory.shared;
    try {
      const data = await networkService.getUsers(query);
      setUsersResponseData(data);
      setUsersError(undefined);
      return data;
    } catch (e: any) {
      setUsersError(e);
    } finally {
      cancelLoading = true;
      setUsersLoading(false);
    }
  };

  const dispatchGetUser = async (userId: number) => {
    const networkService = NetworkServiceFactory.shared;
    try {
      const user = await networkService.getUserIdManager(userId);
      setSelectedUser(user);
      setUsersError(undefined);
    } catch (e: any) {
      setUsersError(e);
    }
  };

  const dispatchGetUserOIDC = async (email: string) => {
    try {
      await NetworkServiceFactory.shared.getUserOidcBridge(email);
      setOidcUserFound(true);
    } catch {
      setOidcUserFound(false);
    }
  };

  const dispatchCreateUser = (query: UserCreateData) => {
    const asyncCall = async () => {
      try {
        const data =
          await NetworkServiceFactory.shared.postUserIdManager(query);
        let oidcSuccess = true;
        try {
          await NetworkServiceFactory.shared.postUserOidcBridge(query);
        } catch {
          oidcSuccess = false;
          notify("warning")({
            message:
              "User created in ID Manager. OIDC Bridge creation failed — please create manually.",
          });
        }
        try {
          if (usersResponseData) {
            const refreshed =
              await NetworkServiceFactory.shared.getUsers(usersResponseData);
            setUsersResponseData(refreshed);
          }
        } catch {
          // Pass
        }
        if (oidcSuccess) {
          notify("success")({ message: "User created!" });
        }
        setSelectedUser(data);
        setUsersError(undefined);
      } catch (e: any) {
        setUsersError(e);
      }
    };
    asyncCall().catch();
  };

  const dispatchUpdateUser = (query: UserUpdateData) => {
    const asyncCall = async () => {
      try {
        const data =
          await NetworkServiceFactory.shared.patchUserIdManager(query);
        let oidcSuccess = true;
        try {
          await NetworkServiceFactory.shared.patchUserOidcBridge(query);
        } catch {
          oidcSuccess = false;
          notify("warning")({
            message:
              "Updated in ID Manager. OIDC Bridge update failed — please update manually.",
          });
        }
        try {
          if (usersResponseData) {
            const refreshed =
              await NetworkServiceFactory.shared.getUsers(usersResponseData);
            setUsersResponseData(refreshed);
          }
        } catch {
          // Pass
        }
        if (oidcSuccess) {
          notify("success")({ message: "User updated!" });
        }
        setSelectedUser(data);
        setUsersError(undefined);
      } catch (e: any) {
        setUsersError(e);
      }
    };
    asyncCall().catch();
  };

  // const dispatchSendUserInvitation = async (query: UserInvitationData) => {
  //   try {
  //     await NetworkServiceFactory.shared.sendUserInvitation(query);
  //     setUsersError(undefined);
  //   } catch (e: any) {
  //     setUsersError(e);
  //   }
  // };

  const dispatchDeleteUser = async (userId: number, email?: string) => {
    try {
      await NetworkServiceFactory.shared.deleteUserIdManager(userId);
      let oidcSuccess = true;
      if (oidcUserFound === true && email) {
        try {
          await NetworkServiceFactory.shared.deleteUserOidcBridge(email);
        } catch {
          oidcSuccess = false;
          notify("warning")({
            message:
              "Deleted from ID Manager. OIDC Bridge deletion failed — please delete manually.",
          });
        }
      }
      if (oidcSuccess) {
        notify("success")({ message: "User deleted!" });
      }
      setUsersError(undefined);
    } catch (e: any) {
      setUsersError(e);
      throw e;
    }
  };

  const dispatchCleanUsersContext = () => {
    setUsersResponseData(undefined);
    setUsersError();
    setSelectedUser();
    setOidcUserFound(undefined);
  };

  return {
    dispatchGetUsers,
    dispatchGetUser,
    dispatchGetUserOIDC,
    dispatchCleanUsersContext,
    dispatchCreateUser,
    dispatchUpdateUser,
    // dispatchSendUserInvitation,
    dispatchDeleteUser,
  };
};

export { useUsersActions };
